import Button from 'ext-ui/components/Button/Button'
import React from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import * as style from 'src/app/CardFeedCreate/CardFeedCreate.style'
import mastercardLogoUrl from 'src/assets/images/issuerLogos/mastercard.svg'
import visaLogoUrl from 'src/assets/images/issuerLogos/visa.svg'
import SvgExternalLink from 'src/components/Svgs/SvgExternalLink'
import { useTranslate } from 'src/localization/Trns'

interface StepContactBankProps {
  onNext: () => void
}

const MASTERCARD_GUIDE_URL =
  'https://docs.astrada.co/page/extend-x-astrada-mastercard-bulk-enrollment-setup-guide-for-corporates#/'
const VISA_GUIDE_URL =
  'https://docs.astrada.co/page/extend-x-astrada-issuer-guide-visa-bulk-enrollment'

const StepContactBank: React.FC<StepContactBankProps> = ({ onNext }) => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()

  const handleGuideClick = (network: 'mastercard' | 'visa'): void => {
    analytics.track('card feeds create', {
      action: 'guide clicked',
      network,
      step: 1,
    })
  }

  const handleNextClick = (): void => {
    analytics.track('card feeds create', { action: 'next clicked', step: 1 })
    onNext()
  }

  return (
    <div>
      <header css={style.stepHeader}>
        <span css={style.stepEyebrow}>{translate('Step 1 of 3')}</span>
        <p css={style.stepHeading}>
          {translate('Contact your bank to enable the feed')}
        </p>
        <p css={style.stepBody}>
          {translate(
            'Extend connects directly with Mastercard and Visa, but your bank has to enable the feed first. Read the full guide linked below before contacting them- it covers everything you and your bank need to do.',
          )
            .split(/(Mastercard|Visa)/g)
            .map((part, i) =>
              part === 'Mastercard' || part === 'Visa' ? (
                <span key={i} css={style.stepBodyEmphasis}>
                  {part}
                </span>
              ) : (
                <React.Fragment key={i}>{part}</React.Fragment>
              ),
            )}
        </p>
      </header>

      <div css={style.guideCardList}>
        <section
          css={style.networkGuideCard}
          aria-labelledby="mastercard-guide-title"
        >
          <div css={style.networkGuideHeader}>
            <p id="mastercard-guide-title" css={style.networkGuideTitle}>
              <img
                src={mastercardLogoUrl}
                alt=""
                width={32}
                height={20}
                css={style.networkGuideLogo}
              />
              {translate('Mastercard Setup Guide')}
            </p>
            <a
              href={MASTERCARD_GUIDE_URL}
              target="_blank"
              rel="noopener noreferrer"
              css={style.guideLinkButton}
              onClick={(): void => handleGuideClick('mastercard')}
              data-testid="card-feed-create-mastercard-guide"
              aria-label={translate('Read full Mastercard setup guide')}
            >
              {translate('Read full guide')}
              <SvgExternalLink
                width={14}
                height={14}
                viewBox="0 0 24 24"
                aria-hidden="true"
              />
            </a>
          </div>
          <p css={style.networkGuideBody}>
            {translate(
              'Steps for you and your bank, including how to find your Delivery ID for card feed connection.',
            )}
          </p>
        </section>

        <section
          css={style.networkGuideCard}
          aria-labelledby="visa-guide-title"
        >
          <div css={style.networkGuideHeader}>
            <p id="visa-guide-title" css={style.networkGuideTitle}>
              <img
                src={visaLogoUrl}
                alt=""
                width={32}
                height={10}
                css={style.networkGuideLogo}
              />
              {translate('Visa Setup Guide')}
            </p>
            <a
              href={VISA_GUIDE_URL}
              target="_blank"
              rel="noopener noreferrer"
              css={style.guideLinkButton}
              onClick={(): void => handleGuideClick('visa')}
              data-testid="card-feed-create-visa-guide"
              aria-label={translate('Read full Visa setup guide')}
            >
              {translate('Read full guide')}
              <SvgExternalLink
                width={14}
                height={14}
                viewBox="0 0 24 24"
                aria-hidden="true"
              />
            </a>
          </div>
          <p css={style.networkGuideBody}>
            {translate(
              'Steps for you and your bank, including how to find your RPIC IDs for card feed connection.',
            )}
          </p>
        </section>
      </div>

      <div css={style.stepActions}>
        <div css={style.stepActionsRight}>
          <Button
            variant="primary"
            onClick={handleNextClick}
            data-testid="card-feed-create-next-1"
          >
            {translate('Next: Enter Feed Details')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default StepContactBank
